import { forwardRef, useEffect, useId, useRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';
import type { UserSummary } from '@blackblack/shared';
import { Icon, type IconName } from './Icon';
import styles from './ui.module.css';

const cx = (...c: Array<string | false | null | undefined>) => c.filter(Boolean).join(' ');
export { cx };

/* ---------- Button ---------- */

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  icon?: IconName;
  loading?: boolean;
  block?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', icon, loading, block, children, className, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      className={cx(styles.btn, styles[`btn_${variant}`], styles[`btn_${size}`], block && styles.block, className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner size={16} /> : icon && <Icon name={icon} size={size === 'sm' ? 16 : 18} />}
      {children && <span>{children}</span>}
    </button>
  );
});

export function IconButton({ icon, label, active, size = 22, className, filled, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { icon: IconName; label: string; active?: boolean; size?: number; filled?: boolean }) {
  return (
    <button className={cx(styles.iconBtn, active && styles.iconBtnActive, className)} aria-label={label} title={label} {...rest}>
      <Icon name={icon} size={size} filled={filled} />
    </button>
  );
}

/* ---------- Avatar ---------- */

export function Avatar({ user, size = 40, ring }: { user: Pick<UserSummary, 'displayName' | 'avatarUrl'>; size?: number; ring?: boolean }) {
  const initials = user.displayName
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <span className={cx(styles.avatar, ring && styles.avatarRing)} style={{ width: size, height: size, fontSize: size * 0.38 }}>
      {user.avatarUrl ? <img src={user.avatarUrl} alt="" loading="lazy" decoding="async" /> : <span aria-hidden="true">{initials || '?'}</span>}
    </span>
  );
}

export function VerifiedBadge({ size = 14 }: { size?: number }) {
  return (
    <span className={styles.verified} title="Geverifieerd">
      <Icon name="verified" size={size} />
      <span className="sr-only">Geverifieerd</span>
    </span>
  );
}

/* ---------- feedback ---------- */

export function Spinner({ size = 24 }: { size?: number }) {
  return <span className={styles.spinner} style={{ width: size, height: size }} role="progressbar" aria-label="Laden" />;
}

export function Skeleton({ w = '100%', h = 16, r = 8, className }: { w?: number | string; h?: number | string; r?: number; className?: string }) {
  return <span className={cx(styles.skeleton, className)} style={{ width: w, height: h, borderRadius: r }} aria-hidden="true" />;
}

export function EmptyState({ icon = 'sparkle', title, text, action }: { icon?: IconName; title: string; text?: string; action?: ReactNode }) {
  return (
    <div className={styles.empty}>
      <span className={styles.emptyIcon}>
        <Icon name={icon} size={28} />
      </span>
      <h3>{title}</h3>
      {text && <p>{text}</p>}
      {action}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const msg = error instanceof Error ? error.message : 'Er ging iets mis';
  return <EmptyState icon="alert" title="Oeps" text={msg} action={onRetry && <Button variant="secondary" icon="refresh" onClick={onRetry}>Opnieuw proberen</Button>} />;
}

/* ---------- form controls ---------- */

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string | undefined;
  hint?: string;
}

export const TextField = forwardRef<HTMLInputElement, FieldProps>(function TextField({ label, error, hint, className, ...rest }, ref) {
  const id = useId();
  return (
    <label className={cx(styles.field, className)} htmlFor={id}>
      <span className={styles.fieldLabel}>{label}</span>
      <input ref={ref} id={id} className={cx(styles.input, error && styles.inputError)} aria-invalid={!!error} aria-describedby={error || hint ? `${id}-d` : undefined} {...rest} />
      {(error || hint) && (
        <span id={`${id}-d`} className={error ? styles.fieldError : styles.fieldHint}>
          {error ?? hint}
        </span>
      )}
    </label>
  );
});

export function TextArea({ label, error, hint, className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string; error?: string; hint?: string }) {
  const id = useId();
  return (
    <label className={cx(styles.field, className)} htmlFor={id}>
      <span className={styles.fieldLabel}>{label}</span>
      <textarea id={id} className={cx(styles.input, styles.textarea, error && styles.inputError)} aria-invalid={!!error} {...rest} />
      {(error || hint) && <span className={error ? styles.fieldError : styles.fieldHint}>{error ?? hint}</span>}
    </label>
  );
}

export function Switch({ checked, onChange, label, description }: { checked: boolean; onChange: (v: boolean) => void; label: string; description?: string }) {
  const id = useId();
  return (
    <div className={styles.switchRow}>
      <label htmlFor={id} className={styles.switchText}>
        <span>{label}</span>
        {description && <small>{description}</small>}
      </label>
      <button id={id} role="switch" aria-checked={checked} className={cx(styles.switch, checked && styles.switchOn)} onClick={() => onChange(!checked)}>
        <span />
      </button>
    </div>
  );
}

/* ---------- tabs ---------- */

export function Tabs<T extends string>({ tabs, value, onChange, sticky }: { tabs: Array<{ id: T; label: string; icon?: IconName }>; value: T; onChange: (v: T) => void; sticky?: boolean }) {
  return (
    <div className={cx(styles.tabs, sticky && styles.tabsSticky)} role="tablist">
      {tabs.map((t) => (
        <button key={t.id} role="tab" aria-selected={t.id === value} className={cx(styles.tab, t.id === value && styles.tabActive)} onClick={() => onChange(t.id)}>
          {t.icon && <Icon name={t.icon} size={18} />}
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function Chips<T extends string>({ items, value, onChange }: { items: Array<{ id: T; label: string }>; value: T; onChange: (v: T) => void }) {
  return (
    <div className={styles.chips} role="tablist">
      {items.map((c) => (
        <button key={c.id} role="tab" aria-selected={c.id === value} className={cx(styles.chip, c.id === value && styles.chipActive)} onClick={() => onChange(c.id)}>
          {c.label}
        </button>
      ))}
    </div>
  );
}

/* ---------- infinite scroll sentinel ---------- */

export function LoadMore({ onVisible, loading, hasMore }: { onVisible: () => void; loading: boolean; hasMore: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const cb = useRef(onVisible);
  cb.current = onVisible;
  useEffect(() => {
    const el = ref.current;
    if (!el || !hasMore || loading) return;
    // Re-created after each load, so it fires again if the sentinel is still in view.
    const io = new IntersectionObserver((e) => e[0]?.isIntersecting && cb.current(), { rootMargin: '600px' });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loading]);
  if (!hasMore && !loading) return null;
  return (
    <div ref={ref} className={styles.loadMore}>
      {loading && <Spinner size={22} />}
    </div>
  );
}
